import { useClient } from "@/composables/useClient";
import { deserializeCbiDto, serializeCbiDto, encodeRequestParam } from "../serialization/CbiDateCodec";
import type { AppDataApi } from "../../api/AppDataApi";
import type { AmericanStateStaff } from "../../generated/dto/AmericanStateStaff";
import type { BoatTypeStaff } from "../../generated/dto/BoatTypeStaff";
import type { CountryStaff } from "../../generated/dto/CountryStaff";
import type { CreateAmericanState } from "../../generated/dto/CreateAmericanState";
import type { CreateBoatType } from "../../generated/dto/CreateBoatType";
import type { CreateCountry } from "../../generated/dto/CreateCountry";
import type { CreateMembershipTypeDiscount } from "../../generated/dto/CreateMembershipTypeDiscount";
import type { CreatePersonType } from "../../generated/dto/CreatePersonType";
import type { CreateSwimProofMethod } from "../../generated/dto/CreateSwimProofMethod";
import type { CreateTag } from "../../generated/dto/CreateTag";
import type { DeleteAmericanStateStaff } from "../../generated/dto/DeleteAmericanStateStaff";
import type { DeleteBoatTypeStaff } from "../../generated/dto/DeleteBoatTypeStaff";
import type { DeleteCountryStaff } from "../../generated/dto/DeleteCountryStaff";
import type { DeleteMembershipTypeDiscountStaff } from "../../generated/dto/DeleteMembershipTypeDiscountStaff";
import type { DeletePersonTypeStaff } from "../../generated/dto/DeletePersonTypeStaff";
import type { DeleteSwimProofMethodStaff } from "../../generated/dto/DeleteSwimProofMethodStaff";
import type { DeleteTagStaff } from "../../generated/dto/DeleteTagStaff";
import type { MembershipTypeDiscountFetchOptions } from "../../generated/dto/MembershipTypeDiscountFetchOptions";
import type { MembershipTypeDiscountStaff } from "../../generated/dto/MembershipTypeDiscountStaff";
import type { MembershipTypeFetchOptions } from "../../generated/dto/MembershipTypeFetchOptions";
import type { MembershipTypeStaff } from "../../generated/dto/MembershipTypeStaff";
import type { PersonTypeStaff } from "../../generated/dto/PersonTypeStaff";
import type { SwimProofMethodStaff } from "../../generated/dto/SwimProofMethodStaff";
import type { TagSearchParams } from "../../generated/dto/TagSearchParams";
import type { TagStaff } from "../../generated/dto/TagStaff";
import type { TagStaffSearchResult } from "../../generated/dto/TagStaffSearchResult";
import type { UpdateAmericanState } from "../../generated/dto/UpdateAmericanState";
import type { UpdateBoatType } from "../../generated/dto/UpdateBoatType";
import type { UpdateCountry } from "../../generated/dto/UpdateCountry";
import type { UpdateMembershipTypeDiscount } from "../../generated/dto/UpdateMembershipTypeDiscount";
import type { UpdatePersonType } from "../../generated/dto/UpdatePersonType";
import type { UpdateSwimProofMethod } from "../../generated/dto/UpdateSwimProofMethod";
import type { UpdateTag } from "../../generated/dto/UpdateTag";
import type { PaginationResult } from "../../generated/dto/PaginationResult";

export class AppDataApiClient implements AppDataApi {
  constructor(private readonly client = useClient()) {}

  async getPersonTypeStaff(id: number): Promise<PersonTypeStaff> {
    const { data } = await this.client.get<unknown>(`/persontype/staff/${id}`);
    return deserializeCbiDto<PersonTypeStaff>(data, "generated.dto.PersonTypeStaff");
  }

  async getTagStaff(id: number): Promise<TagStaff> {
    const { data } = await this.client.get<unknown>(`/tag/staff/${id}`);
    return deserializeCbiDto<TagStaff>(data, "generated.dto.TagStaff");
  }

  async getBoatTypeStaff(id: number): Promise<BoatTypeStaff> {
    const { data } = await this.client.get<unknown>(`/boattype/staff/${id}`);
    return deserializeCbiDto<BoatTypeStaff>(data, "generated.dto.BoatTypeStaff");
  }

  async getCountryStaff(id: number): Promise<CountryStaff> {
    const { data } = await this.client.get<unknown>(`/country/staff/${id}`);
    return deserializeCbiDto<CountryStaff>(data, "generated.dto.CountryStaff");
  }

  async getAmericanStateStaff(id: number): Promise<AmericanStateStaff> {
    const { data } = await this.client.get<unknown>(`/americanstate/staff/${id}`);
    return deserializeCbiDto<AmericanStateStaff>(data, "generated.dto.AmericanStateStaff");
  }

  async getSwimProofMethodStaff(id: number): Promise<SwimProofMethodStaff> {
    const { data } = await this.client.get<unknown>(`/swimproofmethod/staff/${id}`);
    return deserializeCbiDto<SwimProofMethodStaff>(data, "generated.dto.SwimProofMethodStaff");
  }

  async getMembershipTypeDiscountStaff(id: number, options?: MembershipTypeDiscountFetchOptions): Promise<MembershipTypeDiscountStaff> {
    const { data } = await this.client.get<unknown>(`/membershiptypediscount/staff/${id}`, { params: serializeCbiDto(options, "generated.dto.MembershipTypeDiscountFetchOptions") });
    return deserializeCbiDto<MembershipTypeDiscountStaff>(data, "generated.dto.MembershipTypeDiscountStaff");
  }

  async getAllPersonTypeStaffs(): Promise<Array<PersonTypeStaff>> {
    const { data } = await this.client.get<unknown>(`/persontype/staff/all`);
    return deserializeCbiDto<Array<PersonTypeStaff>>(data, "generated.dto.PersonTypeStaff");
  }

  async getAllTagStaffs(): Promise<Array<TagStaff>> {
    const { data } = await this.client.get<unknown>(`/tag/staff/all`);
    return deserializeCbiDto<Array<TagStaff>>(data, "generated.dto.TagStaff");
  }

  async getAllBoatTypeStaffs(): Promise<Array<BoatTypeStaff>> {
    const { data } = await this.client.get<unknown>(`/boattype/staff/all`);
    return deserializeCbiDto<Array<BoatTypeStaff>>(data, "generated.dto.BoatTypeStaff");
  }

  async getAllCountryStaffs(): Promise<Array<CountryStaff>> {
    const { data } = await this.client.get<unknown>(`/country/staff/all`);
    return deserializeCbiDto<Array<CountryStaff>>(data, "generated.dto.CountryStaff");
  }

  async getAllAmericanStateStaffs(): Promise<Array<AmericanStateStaff>> {
    const { data } = await this.client.get<unknown>(`/americanstate/staff/all`);
    return deserializeCbiDto<Array<AmericanStateStaff>>(data, "generated.dto.AmericanStateStaff");
  }

  async getAllSwimProofMethodStaffs(): Promise<Array<SwimProofMethodStaff>> {
    const { data } = await this.client.get<unknown>(`/swimproofmethod/staff/all`);
    return deserializeCbiDto<Array<SwimProofMethodStaff>>(data, "generated.dto.SwimProofMethodStaff");
  }

  async getAllMembershipTypeDiscountStaffs(options?: MembershipTypeDiscountFetchOptions): Promise<Array<MembershipTypeDiscountStaff>> {
    const { data } = await this.client.get<unknown>(`/membershiptypediscount/staff/all`, { params: serializeCbiDto(options, "generated.dto.MembershipTypeDiscountFetchOptions") });
    return deserializeCbiDto<Array<MembershipTypeDiscountStaff>>(data, "generated.dto.MembershipTypeDiscountStaff");
  }

  async createPersonTypeStaff(source: CreatePersonType): Promise<PersonTypeStaff> {
    const { data } = await this.client.post<unknown>(`/persontype/staff`, serializeCbiDto(source, "generated.dto.CreatePersonType"));
    return deserializeCbiDto<PersonTypeStaff>(data, "generated.dto.PersonTypeStaff");
  }

  async createTagStaff(source: CreateTag): Promise<TagStaff> {
    const { data } = await this.client.post<unknown>(`/tag/staff`, serializeCbiDto(source, "generated.dto.CreateTag"));
    return deserializeCbiDto<TagStaff>(data, "generated.dto.TagStaff");
  }

  async createBoatTypeStaff(source: CreateBoatType): Promise<BoatTypeStaff> {
    const { data } = await this.client.post<unknown>(`/boattype/staff`, serializeCbiDto(source, "generated.dto.CreateBoatType"));
    return deserializeCbiDto<BoatTypeStaff>(data, "generated.dto.BoatTypeStaff");
  }

  async createCountryStaff(source: CreateCountry): Promise<CountryStaff> {
    const { data } = await this.client.post<unknown>(`/country/staff`, serializeCbiDto(source, "generated.dto.CreateCountry"));
    return deserializeCbiDto<CountryStaff>(data, "generated.dto.CountryStaff");
  }

  async createAmericanStateStaff(source: CreateAmericanState): Promise<AmericanStateStaff> {
    const { data } = await this.client.post<unknown>(`/americanstate/staff`, serializeCbiDto(source, "generated.dto.CreateAmericanState"));
    return deserializeCbiDto<AmericanStateStaff>(data, "generated.dto.AmericanStateStaff");
  }

  async createSwimProofMethodStaff(source: CreateSwimProofMethod): Promise<SwimProofMethodStaff> {
    const { data } = await this.client.post<unknown>(`/swimproofmethod/staff`, serializeCbiDto(source, "generated.dto.CreateSwimProofMethod"));
    return deserializeCbiDto<SwimProofMethodStaff>(data, "generated.dto.SwimProofMethodStaff");
  }

  async createMembershipTypeDiscountStaff(source: CreateMembershipTypeDiscount): Promise<MembershipTypeDiscountStaff> {
    const { data } = await this.client.post<unknown>(`/membershiptypediscount/staff`, serializeCbiDto(source, "generated.dto.CreateMembershipTypeDiscount"));
    return deserializeCbiDto<MembershipTypeDiscountStaff>(data, "generated.dto.MembershipTypeDiscountStaff");
  }

  async updatePersonTypeStaff(source: UpdatePersonType): Promise<PersonTypeStaff> {
    const { data } = await this.client.put<unknown>(`/persontype/staff/${source.typeId}`, serializeCbiDto(source, "generated.dto.UpdatePersonType"));
    return deserializeCbiDto<PersonTypeStaff>(data, "generated.dto.PersonTypeStaff");
  }

  async updateTagStaff(source: UpdateTag): Promise<TagStaff> {
    const { data } = await this.client.put<unknown>(`/tag/staff/${source.tagId}`, serializeCbiDto(source, "generated.dto.UpdateTag"));
    return deserializeCbiDto<TagStaff>(data, "generated.dto.TagStaff");
  }

  async updateBoatTypeStaff(source: UpdateBoatType): Promise<BoatTypeStaff> {
    const { data } = await this.client.put<unknown>(`/boattype/staff/${source.boatId}`, serializeCbiDto(source, "generated.dto.UpdateBoatType"));
    return deserializeCbiDto<BoatTypeStaff>(data, "generated.dto.BoatTypeStaff");
  }

  async updateCountryStaff(source: UpdateCountry): Promise<CountryStaff> {
    const { data } = await this.client.put<unknown>(`/country/staff/${source.countryId}`, serializeCbiDto(source, "generated.dto.UpdateCountry"));
    return deserializeCbiDto<CountryStaff>(data, "generated.dto.CountryStaff");
  }

  async updateAmericanStateStaff(source: UpdateAmericanState): Promise<AmericanStateStaff> {
    const { data } = await this.client.put<unknown>(`/americanstate/staff/${source.stateId}`, serializeCbiDto(source, "generated.dto.UpdateAmericanState"));
    return deserializeCbiDto<AmericanStateStaff>(data, "generated.dto.AmericanStateStaff");
  }

  async updateSwimProofMethodStaff(source: UpdateSwimProofMethod): Promise<SwimProofMethodStaff> {
    const { data } = await this.client.put<unknown>(`/swimproofmethod/staff/${source.methodId}`, serializeCbiDto(source, "generated.dto.UpdateSwimProofMethod"));
    return deserializeCbiDto<SwimProofMethodStaff>(data, "generated.dto.SwimProofMethodStaff");
  }

  async updateMembershipTypeDiscountStaff(source: UpdateMembershipTypeDiscount): Promise<MembershipTypeDiscountStaff> {
    const { data } = await this.client.put<unknown>(`/membershiptypediscount/staff/${source.discountId}`, serializeCbiDto(source, "generated.dto.UpdateMembershipTypeDiscount"));
    return deserializeCbiDto<MembershipTypeDiscountStaff>(data, "generated.dto.MembershipTypeDiscountStaff");
  }

  async deletePersonTypeStaff(source: DeletePersonTypeStaff): Promise<void> {
    await this.client.delete(`/persontype/staff/${source.typeId}`);
  }

  async deleteTagStaff(source: DeleteTagStaff): Promise<void> {
    await this.client.delete(`/tag/staff/${source.tagId}`);
  }

  async deleteBoatTypeStaff(source: DeleteBoatTypeStaff): Promise<void> {
    await this.client.delete(`/boattype/staff/${source.boatId}`);
  }

  async deleteCountryStaff(source: DeleteCountryStaff): Promise<void> {
    await this.client.delete(`/country/staff/${source.countryId}`);
  }

  async deleteAmericanStateStaff(source: DeleteAmericanStateStaff): Promise<void> {
    await this.client.delete(`/americanstate/staff/${source.stateId}`);
  }

  async deleteSwimProofMethodStaff(source: DeleteSwimProofMethodStaff): Promise<void> {
    await this.client.delete(`/swimproofmethod/staff/${source.methodId}`);
  }

  async deleteMembershipTypeDiscountStaff(source: DeleteMembershipTypeDiscountStaff): Promise<void> {
    await this.client.delete(`/membershiptypediscount/staff/${source.discountId}`);
  }

  async searchTagStaff(params: TagSearchParams, page: number, perPage: number): Promise<PaginationResult<TagStaffSearchResult>> {
    const { data } = await this.client.post<unknown>(`/tag/staff/search`, serializeCbiDto(params, "generated.dto.TagSearchParams"), { params: { page, perPage } });
    let result = data as PaginationResult<TagStaffSearchResult>;
    result = { ...result, items: deserializeCbiDto<Array<TagStaffSearchResult>>(result.items, "generated.dto.TagStaffSearchResult") };
    return result;
  }

  async searchTagStaffFull(params: TagSearchParams, page: number, perPage: number): Promise<PaginationResult<TagStaff>> {
    const { data } = await this.client.post<unknown>(`/tag/staff/search/full`, serializeCbiDto(params, "generated.dto.TagSearchParams"), { params: { page, perPage } });
    let result = data as PaginationResult<TagStaff>;
    result = { ...result, items: deserializeCbiDto<Array<TagStaff>>(result.items, "generated.dto.TagStaff") };
    return result;
  }

  async getMembershipTypeDiscountStaffMembershipTypeId(id: number, options?: MembershipTypeFetchOptions): Promise<MembershipTypeStaff | null> {
    const { data } = await this.client.get<unknown>(`/membershiptypediscount/staff/${id}/membershiptypeid`, { params: serializeCbiDto(options, "generated.dto.MembershipTypeFetchOptions") });
    return deserializeCbiDto<MembershipTypeStaff | null>(data, "generated.dto.MembershipTypeStaff");
  }
}

export const useAppDataApi = (): AppDataApi => new AppDataApiClient();
